// panZoom.js — the maths of a pan/zoom surface, with no DOM in it.
// ---------------------------------------------------------------------------
// 2026-09-14. Brett, on the shared client report: "i cant close a photo unless
// i am fully zoomed out." The photo viewer claimed the pointer on pointerDOWN
// whenever it was zoomed, so a press on the × (or ‹ › or Reset) was captured by
// the pan surface and the button never received its click. The plan viewer on
// the same page had already learned this — a pin's click was stolen the same
// way — and fixed it by claiming the pointer only once a real DRAG begins. Two
// copies of one idea, one of them still wrong: this file and usePanZoom.js
// make it one idea. Every pan surface reads its rules here.

/** The fitted view: no zoom, no offset. */
export const FIT = Object.freeze({ scale: 1, tx: 0, ty: 0 });

/** A press that travels further than this is a drag, not a click. */
export const DRAG_THRESHOLD_PX = 3;

/** Wheel notch: 15 % in or out. */
export const WHEEL_STEP = 1.15;

/** Keep the surface covering the box: scale ≥ 1, no gap at any edge. */
export function clampView(v, W, H) {
  const s = Math.max(1, Number(v.scale) || 1);
  return {
    scale: s,
    tx: Math.min(0, Math.max(W - W * s, Number(v.tx) || 0)),
    ty: Math.min(0, Math.max(H - H * s, Number(v.ty) || 0)),
  };
}

/** Zoom by `factor` keeping the point under the cursor (px,py — box pixels) still. */
export function zoomAt(v, px, py, factor, W, H, maxScale = 6) {
  const scale = Math.min(maxScale, Math.max(1, v.scale * factor));
  const lx = (px - v.tx) / v.scale, ly = (py - v.ty) / v.scale;
  return clampView({ scale, tx: px - lx * scale, ty: py - ly * scale }, W, H);
}

/** A view at scale S with the surface point (fx,fy — fractions of the box) at the centre. */
export function viewCentredOn(W, H, S, fx = 0.5, fy = 0.5) {
  return clampView({ scale: S, tx: W / 2 - fx * W * S, ty: H / 2 - fy * H * S }, W, H);
}

/** Has the pointer moved far enough since the press to be a drag? */
export function isDragStart(dx, dy) {
  return Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX;
}
